/**
 * Cinematic Pipeline — The main orchestrator.
 *
 * Full Pipeline:
 *   User Prompt → Intent Extractor → Reference Analyzer → Inference Engine
 *   → Conflict Detector → Prompt Optimizer → Final Prompt
 *
 * 3 Layers:
 *   Layer 1: Raw Prompt (never changed)
 *   Layer 2: Semantic Analysis (SceneGraph with user + inferred fields)
 *   Layer 3: Final Cinematic Prompt (professional output)
 */

import type { SceneGraph } from './sceneGraph';
import { mergeSceneGraphs } from './sceneGraph';
import { extractIntent } from './intentExtractor';
import { runInference } from './inferenceEngine';
import { buildFinalPrompt, buildAnalysisView } from './promptOptimizer';
import { applyReferenceToGraph, analyzeReferenceDescription, type ReferenceAnalysis } from './referenceAnalyzer';
import { parseCharacterEdit, applyCharacterEdit, isCharacterEditCommand } from './characterEditor';
import { detectAndResolveConflicts, type SceneConflict } from './conflictDetectorEngine';

export interface PipelineResult {
  /** Layer 1 — User's raw input, preserved exactly */
  rawPrompt: string;

  /** Layer 2 — Parsed + inferred scene graph */
  sceneGraph: SceneGraph;

  /** Layer 3 — Final cinematic prompt */
  finalPrompt: string;

  /** Debug: what was user-specified, inferred, or left null */
  analysis: {
    userSpecified: Record<string, string>;
    inferred: Record<string, string>;
    nullFields: string[];
  };

  /** Conflicts detected and resolved */
  conflicts: SceneConflict[];

  /** Reference analysis (if any) */
  referenceAnalysis?: ReferenceAnalysis;
}

/**
 * Run the full cinematic pipeline on a raw user prompt.
 * This is a FULL parse — all fields start as NULL, only what's detected gets set.
 *
 * Pipeline: Intent → Reference → Inference → Conflict Detection → Optimize
 */
export function runCinematicPipeline(
  rawPrompt: string,
  referenceDescription?: string
): PipelineResult {
  // Step 1: Extract intent → SceneGraph with NULLs
  const extracted = extractIntent(rawPrompt);

  // Step 2: Apply reference analysis (if provided)
  let afterReference = extracted;
  let refAnalysis: ReferenceAnalysis | undefined;
  if (referenceDescription) {
    refAnalysis = analyzeReferenceDescription(referenceDescription);
    afterReference = applyReferenceToGraph(extracted, refAnalysis);
  }

  // Step 3: Run inference → fill in contextual defaults
  const inferred = runInference(afterReference);

  // Step 4: Detect and resolve conflicts
  const { graph: resolved, conflicts } = detectAndResolveConflicts(inferred);

  // Step 5: Build final cinematic prompt
  const finalPrompt = buildFinalPrompt(resolved);

  // Step 6: Build analysis view
  const analysis = buildAnalysisView(resolved);

  return {
    rawPrompt,
    sceneGraph: resolved,
    finalPrompt,
    analysis,
    conflicts,
    referenceAnalysis: refAnalysis,
  };
}

/**
 * Run incremental update — merge new user edits into existing scene graph.
 * Supports both regular edits AND character-specific edit commands.
 * RULE: DO NOT OVERRIDE USER INTENT
 */
export function runIncrementalUpdate(
  existingGraph: SceneGraph,
  editPrompt: string
): PipelineResult {
  let merged: SceneGraph;

  // Check if this is a character edit command
  if (isCharacterEditCommand(editPrompt)) {
    const edit = parseCharacterEdit(editPrompt)!;
    const editResult = applyCharacterEdit(existingGraph, edit);

    // Create merged graph with updated character
    merged = {
      ...existingGraph,
      character: editResult.character,
      rawPrompt: `${existingGraph.rawPrompt}\n${editPrompt}`,
      meta: {
        ...existingGraph.meta,
        userSpecifiedFields: [
          ...existingGraph.meta.userSpecifiedFields,
          ...editResult.changedFields,
        ],
      },
    };
  } else {
    // Regular prompt edit — extract and merge
    const editExtracted = extractIntent(editPrompt);
    merged = mergeSceneGraphs(existingGraph, editExtracted);
    merged.rawPrompt = `${existingGraph.rawPrompt}\n${editPrompt}`;
  }

  // Re-run inference with merged graph
  const inferred = runInference(merged);

  // Detect conflicts
  const { graph: resolved, conflicts } = detectAndResolveConflicts(inferred);

  // Build final prompt
  const finalPrompt = buildFinalPrompt(resolved);
  const analysis = buildAnalysisView(resolved);

  return {
    rawPrompt: merged.rawPrompt,
    sceneGraph: resolved,
    finalPrompt,
    analysis,
    conflicts,
  };
}

/**
 * Convert SceneGraph to canvas element properties.
 * Returns property patches for camera, light, and character elements.
 * NULL values → element defaults (none/neutral).
 */
export function sceneGraphToCanvasProperties(graph: SceneGraph): {
  camera: Record<string, string>;
  light: Record<string, string>;
  character: Record<string, string> | null;
  globalSettings: Record<string, string | number | boolean>;
} {
  // Camera
  const camera: Record<string, string> = {
    lens: graph.camera.lens || '50mm',
    perspective: graph.camera.angle || 'eye_level',
    shotType: graph.camera.shotType || 'medium',
    dof: graph.camera.dof || 'moderate',
    cameraMovement: graph.camera.movement || 'static',
    cameraType: 'dslr',
    focusMode: 'subject',
    sensorSize: 'full_frame',
    shutterSpeed: '1/125',
    isoControl: '100',
    motionBlur: '0',
    dofIntensity: '50',
  };

  // Light
  const lightStyleMap: Record<string, string> = {
    dramatic: 'dramatic', soft: 'natural', neon: 'dramatic',
    moonlight: 'dramatic', sunlight: 'natural', backlight: 'dramatic',
    volumetric: 'dramatic', natural: 'natural', candlelight: 'natural',
    spotlight: 'dramatic',
  };
  const lightPresetMap: Record<string, string> = {
    golden_hour: 'golden_hour', blue_hour: 'blue_hour',
  };

  const light: Record<string, string> = {
    lightStyle: lightStyleMap[graph.lighting.type || 'natural'] || 'natural',
    lightPreset: lightPresetMap[graph.lighting.preset || 'none'] || 'none',
    intensity: graph.lighting.intensity === 'low_key' ? '35'
      : graph.lighting.intensity === 'high_key' ? '80' : '55',
    colorTemp: graph.lighting.colorTemp === 'warm' ? 'warm'
      : graph.lighting.colorTemp === 'cool' ? 'cool' : 'neutral',
    lightModifier: 'none',
    colorTempValue: graph.lighting.colorTemp === 'warm' ? '3200'
      : graph.lighting.colorTemp === 'cool' ? '7500' : '5500',
    shadowSoftness: '50',
    lightDirection: '0',
    godRays: 'false',
    fogDensity: graph.environment.atmosphere?.includes('fog') ? '40' : '0',
  };

  // Character (null if no character detected)
  let character: Record<string, string> | null = null;
  if (graph.character.type) {
    const emotionMap: Record<string, string> = {
      laughing: 'happy', smiling: 'happy', crying: 'sad',
      angry: 'angry', sad: 'sad', scared: 'scared', happy: 'happy',
      menacing: 'angry', surprised: 'surprised', confident: 'confident',
      mysterious: 'neutral', neutral: 'neutral',
    };

    character = {
      subject: graph.character.type,
      expression: emotionMap[graph.character.emotion || 'neutral'] || 'neutral',
      pose: graph.character.pose || 'standing',
      characterFraming: graph.camera.shotType === 'close_up' ? 'close_up' : 'full',
      microExpression: 'none',
      eyeDirection: 'camera',
      bodyRotation: '0',
      gestureIntensity: '50',
    };
  }

  // Global settings
  const globalSettings: Record<string, string | number | boolean> = {
    ruleOfThirds: graph.camera.shotType !== 'close_up',
    centeredCinematic: graph.camera.shotType === 'close_up',
    atmosphericDensity: graph.environment.atmosphere?.includes('fog') ? 45 : 20,
    godRaysGlobal: graph.lighting.type === 'volumetric' ? 40 : 10,
    filmStock: 'none',
    filmGrain: 0,
  };

  return { camera, light, character, globalSettings };
}
